import { getDisableFormConfig } from './buildHiddenTab/getDisableFormConfig';
import { getHideFormConfig } from './buildHiddenTab/getHideFormConfig';
/**
 * Constructs a conditional visibility settings tab for a form widget, allowing the user
 * to define logic for hiding/showing components based on values in sibling components.
 *
 * @param {WidgetProps<any, any>} props - The widget properties passed from the editor context.
 * @param {object} [params] - Optional parameters for customizing the tab.
 * @param {DefaultFormWidgetConfigTypes[]} [params.children] - Additional custom child components to include.
 * @param {(data: any) => any} [params.onLoad] - Optional hook to transform data on tab load.
 * @param {(data: any) => any} [params.onSubmit] - Optional hook to transform data on tab submit.
 * @returns {EditorTab} The constructed tab definition for the conditional visibility editor.
 */
export function buildHiddenTab(props, params) {
    var _a;
    const t = params === null || params === void 0 ? void 0 : params.t;
    const parentConfig = (_a = props.parentProps) === null || _a === void 0 ? void 0 : _a.config;
    const siblingCheckboxComponents = [];
    const siblingYesNoComponents = [];
    const siblingNumberComponents = [];
    const siblingTextComponents = [];
    const siblingRadioComponents = [];
    const siblingRadioConfigs = {};
    const siblingSelectComponents = [];
    const siblingSelectConfigs = {};
    const selectComponentValues = [];
    const siblingYesNoConfigs = {};
    if (parentConfig) {
        const parentWidget = props.widgets.find((w) => w.type === parentConfig.type);
        if (parentWidget && parentWidget.childConfigs) {
            parentWidget.childConfigs(parentConfig).forEach((siblingConfig) => {
                if (siblingConfig !== props.config && siblingConfig.key) {
                    const component = '^' + siblingConfig.key;
                    switch (siblingConfig.type) {
                        case 'form.checkbox':
                            siblingCheckboxComponents.push(component);
                            selectComponentValues.push({ value: component, label: 'Checkbox: ' + siblingConfig.label });
                            break;
                        case 'form.number':
                            siblingNumberComponents.push(component);
                            selectComponentValues.push({ value: component, label: 'Number: ' + siblingConfig.label });
                            break;
                        case 'form.text':
                            siblingTextComponents.push(component);
                            selectComponentValues.push({ value: component, label: 'Text: ' + siblingConfig.label });
                            break;
                        case 'form.radio':
                            siblingRadioComponents.push(component);
                            siblingRadioConfigs[component] = siblingConfig;
                            selectComponentValues.push({ value: component, label: 'Radio: ' + siblingConfig.label });
                            break;
                        case 'form.select':
                            siblingSelectComponents.push(component);
                            siblingSelectConfigs[component] = siblingConfig;
                            selectComponentValues.push({ value: component, label: 'Select: ' + siblingConfig.label });
                            break;
                        case 'form.yesNo':
                            siblingYesNoComponents.push(component);
                            siblingYesNoConfigs[component] = siblingConfig;
                            selectComponentValues.push({ value: component, label: 'Select: ' + siblingConfig.label });
                            break;
                    }
                }
            });
        }
        selectComponentValues.push({ value: '$', label: 'Other' });
    }
    const children = getHideFormConfig({
        t,
        selectComponentValues,
        siblingSelectComponents,
        siblingYesNoComponents,
        siblingNumberComponents,
        siblingRadioComponents,
        siblingTextComponents,
        siblingRadioConfigs,
        siblingSelectConfigs,
        siblingCheckboxComponents,
        handleUpdateAnyValuesBasedOnSiblingConfig: onUpdateAnyValuesBasedOnSiblingConfig,
    });
    if (params === null || params === void 0 ? void 0 : params.allowDisable) {
        children.push(...getDisableFormConfig({
            t,
            selectComponentValues,
            siblingSelectComponents,
            siblingYesNoComponents,
            siblingNumberComponents,
            siblingRadioComponents,
            siblingTextComponents,
            siblingRadioConfigs,
            siblingSelectConfigs,
            siblingCheckboxComponents,
            handleUpdateAnyValuesBasedOnSiblingConfig: onUpdateAnyValuesBasedOnSiblingConfig,
        }));
    }
    if (params === null || params === void 0 ? void 0 : params.children) {
        children.push(...params.children);
    }
    const onLoad = (data) => {
        // Hidden config mapping
        data.hiddenConfig = { type: 'visible' };
        if (typeof data.hidden === 'object') {
            let dataEq;
            let dataIn;
            if (typeof data.hidden.custom === 'string') {
                data.hiddenConfig.type = 'custom';
                data.hiddenConfig.custom = data.hidden.custom;
            }
            else if (data.hidden.dataEq != null) {
                data.hiddenConfig.type = 'hide';
                dataEq = data.hidden.dataEq;
            }
            else if (data.hidden.dataIn != null) {
                data.hiddenConfig.type = 'hide';
                dataIn = data.hidden.dataIn;
            }
            else if (data.hidden.dataNotEq != null) {
                data.hiddenConfig.type = 'show';
                dataEq = data.hidden.dataNotEq;
            }
            else if (data.hidden.dataNotIn != null) {
                data.hiddenConfig.type = 'show';
                dataIn = data.hidden.dataNotIn;
            }
            if (dataEq != null && data.hidden.when) {
                if (typeof dataEq === 'boolean' && siblingCheckboxComponents.includes(data.hidden.when)) {
                    data.hiddenConfig.component = data.hidden.when;
                    data.hiddenConfig.dataEqCheckbox = dataEq;
                }
                else if (typeof dataEq === 'number' && siblingNumberComponents.includes(data.hidden.when)) {
                    data.hiddenConfig.component = data.hidden.when;
                    data.hiddenConfig.dataEqNumber = dataEq;
                }
                else if (typeof dataEq === 'boolean' && siblingYesNoComponents.includes(data.hidden.when)) {
                    data.hiddenConfig.component = data.hidden.when;
                    data.hiddenConfig.dataEqYesNo = dataEq;
                }
                else if (typeof dataEq === 'string' && siblingTextComponents.includes(data.hidden.when)) {
                    data.hiddenConfig.component = data.hidden.when;
                    data.hiddenConfig.dataEqText = dataEq;
                }
                else if (siblingRadioComponents.includes(data.hidden.when)) {
                    data.hiddenConfig.component = data.hidden.when;
                    data.hiddenConfig.dataInCheckboxList = [dataEq];
                }
                else if (siblingSelectComponents.includes(data.hidden.when)) {
                    data.hiddenConfig.component = data.hidden.when;
                    data.hiddenConfig.dataInMultiSelect = [dataEq];
                }
                else {
                    data.hiddenConfig.component = '$';
                    data.hiddenConfig.when = data.hidden.when;
                    switch (typeof dataEq) {
                        case 'boolean':
                            data.hiddenConfig.whenDataEqType = dataEq ? 'true' : 'false';
                            break;
                        case 'number':
                            data.hiddenConfig.whenDataEqType = 'number';
                            data.hiddenConfig.whenDataEqNumber = dataEq;
                            break;
                        case 'string':
                            data.hiddenConfig.whenDataEqType = 'text';
                            data.hiddenConfig.whenDataEqText = dataEq;
                            break;
                        default:
                            data.hiddenConfig.whenDataEqType = 'eqJson';
                            data.hiddenConfig.whenDataEqJson = JSON.stringify(dataEq, undefined, '  ');
                    }
                }
            }
            if (dataIn != null && data.hidden.when) {
                if (siblingRadioComponents.includes(data.hidden.when)) {
                    data.hiddenConfig.component = data.hidden.when;
                    data.hiddenConfig.dataInCheckboxList = dataIn;
                }
                else if (siblingSelectComponents.includes(data.hidden.when)) {
                    data.hiddenConfig.component = data.hidden.when;
                    data.hiddenConfig.dataInMultiSelect = dataIn;
                }
                else {
                    data.hiddenConfig.component = '$';
                    data.hiddenConfig.when = data.hidden.when;
                    data.hiddenConfig.whenDataEqType = 'inJson';
                    data.hiddenConfig.whenDataInJson = JSON.stringify(dataIn, undefined, '  ');
                }
            }
        }
        // Disabled config mapping
        data.disabledConfig = { type: 'enabled' };
        if (typeof data.disabledCondition === 'object') {
            const disabled = data.disabledCondition;
            let dataEqD;
            let dataInD;
            if (typeof disabled.custom === 'string') {
                data.disabledConfig.type = 'custom';
                data.disabledConfig.custom = disabled.custom;
            }
            else if (disabled.dataEq != null) {
                data.disabledConfig.type = 'disable';
                dataEqD = disabled.dataEq;
            }
            else if (disabled.dataIn != null) {
                data.disabledConfig.type = 'disable';
                dataInD = disabled.dataIn;
            }
            else if (disabled.dataNotEq != null) {
                data.disabledConfig.type = 'enable';
                dataEqD = disabled.dataNotEq;
            }
            else if (disabled.dataNotIn != null) {
                data.disabledConfig.type = 'enable';
                dataInD = disabled.dataNotIn;
            }
            if (dataEqD != null && disabled.when) {
                if (typeof dataEqD === 'boolean' && siblingCheckboxComponents.includes(disabled.when)) {
                    data.disabledConfig.component = disabled.when;
                    data.disabledConfig.dataEqCheckbox = dataEqD;
                }
                else if (typeof dataEqD === 'number' && siblingNumberComponents.includes(disabled.when)) {
                    data.disabledConfig.component = disabled.when;
                    data.disabledConfig.dataEqNumber = dataEqD;
                }
                else if (typeof dataEqD === 'boolean' && siblingYesNoComponents.includes(disabled.when)) {
                    data.disabledConfig.component = disabled.when;
                    data.disabledConfig.dataEqYesNo = dataEqD;
                }
                else if (typeof dataEqD === 'string' && siblingTextComponents.includes(disabled.when)) {
                    data.disabledConfig.component = disabled.when;
                    data.disabledConfig.dataEqText = dataEqD;
                }
                else if (siblingRadioComponents.includes(disabled.when)) {
                    data.disabledConfig.component = disabled.when;
                    data.disabledConfig.dataInCheckboxList = [dataEqD];
                }
                else if (siblingSelectComponents.includes(disabled.when)) {
                    data.disabledConfig.component = disabled.when;
                    data.disabledConfig.dataInMultiSelect = [dataEqD];
                }
                else {
                    data.disabledConfig.component = '$';
                    data.disabledConfig.when = disabled.when;
                    switch (typeof dataEqD) {
                        case 'boolean':
                            data.disabledConfig.whenDataEqType = dataEqD ? 'true' : 'false';
                            break;
                        case 'number':
                            data.disabledConfig.whenDataEqType = 'number';
                            data.disabledConfig.whenDataEqNumber = dataEqD;
                            break;
                        case 'string':
                            data.disabledConfig.whenDataEqType = 'text';
                            data.disabledConfig.whenDataEqText = dataEqD;
                            break;
                        default:
                            data.disabledConfig.whenDataEqType = 'eqJson';
                            data.disabledConfig.whenDataEqJson = JSON.stringify(dataEqD, undefined, '  ');
                    }
                }
            }
            if (dataInD != null && disabled.when) {
                if (siblingRadioComponents.includes(disabled.when)) {
                    data.disabledConfig.component = disabled.when;
                    data.disabledConfig.dataInCheckboxList = dataInD;
                }
                else if (siblingSelectComponents.includes(disabled.when)) {
                    data.disabledConfig.component = disabled.when;
                    data.disabledConfig.dataInMultiSelect = dataInD;
                }
                else {
                    data.disabledConfig.component = '$';
                    data.disabledConfig.when = disabled.when;
                    data.disabledConfig.whenDataEqType = 'inJson';
                    data.disabledConfig.whenDataInJson = JSON.stringify(dataInD, undefined, '  ');
                }
            }
        }
        else if (data.disabled) {
            data.disabledConfig.type = 'disabled';
        }
        if (params === null || params === void 0 ? void 0 : params.onLoad) {
            data = params.onLoad(data);
        }
        return data;
    };
    const onSubmit = (data) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
        // Hidden
        if (((_a = data.hiddenConfig) === null || _a === void 0 ? void 0 : _a.type) === 'hide' || ((_b = data.hiddenConfig) === null || _b === void 0 ? void 0 : _b.type) === 'show') {
            let when;
            let dataEq;
            let dataIn;
            if (data.hiddenConfig.component && data.hiddenConfig.component !== '$') {
                if (siblingCheckboxComponents.includes(data.hiddenConfig.component)) {
                    when = data.hiddenConfig.component;
                    dataEq = data.hiddenConfig.dataEqCheckbox;
                }
                else if (siblingNumberComponents.includes(data.hiddenConfig.component)) {
                    when = data.hiddenConfig.component;
                    dataEq = data.hiddenConfig.dataEqNumber;
                }
                else if (siblingYesNoComponents.includes(data.hiddenConfig.component)) {
                    when = data.hiddenConfig.component;
                    dataEq = data.hiddenConfig.dataEqYesNo;
                }
                else if (siblingTextComponents.includes(data.hiddenConfig.component)) {
                    when = data.hiddenConfig.component;
                    dataEq = data.hiddenConfig.dataEqText;
                }
                else if (siblingRadioComponents.includes(data.hiddenConfig.component)) {
                    when = data.hiddenConfig.component;
                    dataIn = data.hiddenConfig.dataInCheckboxList;
                }
                else if (siblingSelectComponents.includes(data.hiddenConfig.component)) {
                    when = data.hiddenConfig.component;
                    dataIn = data.hiddenConfig.dataInMultiSelect;
                }
            }
            else if (data.hiddenConfig.component === '$' && data.hiddenConfig.when) {
                when = data.hiddenConfig.when;
                switch (data.hiddenConfig.whenDataEqType) {
                    case 'text':
                        dataEq = data.hiddenConfig.whenDataEqText;
                        break;
                    case 'number':
                        dataEq = data.hiddenConfig.whenDataEqNumber;
                        break;
                    case 'true':
                        dataEq = true;
                        break;
                    case 'false':
                        dataEq = false;
                        break;
                    case 'eqJson':
                        dataEq = JSON.parse((_c = data.hiddenConfig.whenDataEqJson) !== null && _c !== void 0 ? _c : 'null');
                        break;
                    case 'inJson':
                        dataIn = JSON.parse((_d = data.hiddenConfig.whenDataInJson) !== null && _d !== void 0 ? _d : '[]');
                        break;
                }
            }
            if (when && (dataEq != null || dataIn)) {
                data.hidden = {
                    when: when,
                };
                if (dataIn && dataIn.length === 1) {
                    dataEq = dataIn[0];
                }
                if (dataEq != null) {
                    if (((_e = data.hiddenConfig) === null || _e === void 0 ? void 0 : _e.type) === 'hide') {
                        data.hidden.dataEq = dataEq;
                    }
                    else {
                        data.hidden.dataNotEq = dataEq;
                    }
                }
                else {
                    if (((_f = data.hiddenConfig) === null || _f === void 0 ? void 0 : _f.type) === 'hide') {
                        data.hidden.dataIn = dataIn;
                    }
                    else {
                        data.hidden.dataNotIn = dataIn;
                    }
                }
            }
        }
        else if (((_g = data.hiddenConfig) === null || _g === void 0 ? void 0 : _g.type) === 'custom') {
            data.hidden = {
                custom: (_h = data.hiddenConfig) === null || _h === void 0 ? void 0 : _h.custom,
            };
        }
        else {
            delete data.hidden;
            delete data.clearDataOnHide;
        }
        delete data.hiddenConfig;
        // Disabled
        if (((_j = data.disabledConfig) === null || _j === void 0 ? void 0 : _j.type) === 'disable' || ((_k = data.disabledConfig) === null || _k === void 0 ? void 0 : _k.type) === 'enable') {
            let whenD;
            let dataEqD;
            let dataInD;
            if (data.disabledConfig.component && data.disabledConfig.component !== '$') {
                if (siblingCheckboxComponents.includes(data.disabledConfig.component)) {
                    whenD = data.disabledConfig.component;
                    dataEqD = data.disabledConfig.dataEqCheckbox;
                }
                else if (siblingNumberComponents.includes(data.disabledConfig.component)) {
                    whenD = data.disabledConfig.component;
                    dataEqD = data.disabledConfig.dataEqNumber;
                }
                else if (siblingYesNoComponents.includes(data.disabledConfig.component)) {
                    whenD = data.disabledConfig.component;
                    dataEqD = data.disabledConfig.dataEqYesNo;
                }
                else if (siblingTextComponents.includes(data.disabledConfig.component)) {
                    whenD = data.disabledConfig.component;
                    dataEqD = data.disabledConfig.dataEqText;
                }
                else if (siblingRadioComponents.includes(data.disabledConfig.component)) {
                    whenD = data.disabledConfig.component;
                    dataInD = data.disabledConfig.dataInCheckboxList;
                }
                else if (siblingSelectComponents.includes(data.disabledConfig.component)) {
                    whenD = data.disabledConfig.component;
                    dataInD = data.disabledConfig.dataInMultiSelect;
                }
            }
            else if (data.disabledConfig.component === '$' && data.disabledConfig.when) {
                whenD = data.disabledConfig.when;
                switch (data.disabledConfig.whenDataEqType) {
                    case 'text':
                        dataEqD = data.disabledConfig.whenDataEqText;
                        break;
                    case 'number':
                        dataEqD = data.disabledConfig.whenDataEqNumber;
                        break;
                    case 'true':
                        dataEqD = true;
                        break;
                    case 'false':
                        dataEqD = false;
                        break;
                    case 'eqJson':
                        dataEqD = JSON.parse((_l = data.disabledConfig.whenDataEqJson) !== null && _l !== void 0 ? _l : 'null');
                        break;
                    case 'inJson':
                        dataInD = JSON.parse((_m = data.disabledConfig.whenDataInJson) !== null && _m !== void 0 ? _m : '[]');
                        break;
                }
            }
            if (whenD && (dataEqD != null || dataInD)) {
                data.disabledCondition = {
                    when: whenD,
                };
                if (dataInD && dataInD.length === 1) {
                    dataEqD = dataInD[0];
                }
                if (dataEqD != null) {
                    if (((_o = data.disabledConfig) === null || _o === void 0 ? void 0 : _o.type) === 'disable') {
                        data.disabledCondition.dataEq = dataEqD;
                    }
                    else {
                        data.disabledCondition.dataNotEq = dataEqD;
                    }
                }
                else {
                    if (((_p = data.disabledConfig) === null || _p === void 0 ? void 0 : _p.type) === 'disable') {
                        data.disabledCondition.dataIn = dataInD;
                    }
                    else {
                        data.disabledCondition.dataNotIn = dataInD;
                    }
                }
            }
        }
        else if (((_q = data.disabledConfig) === null || _q === void 0 ? void 0 : _q.type) === 'custom') {
            data.disabledCondition = {
                custom: (_r = data.disabledConfig) === null || _r === void 0 ? void 0 : _r.custom,
            };
        }
        else if (((_s = data.disabledConfig) === null || _s === void 0 ? void 0 : _s.type) === 'disabled') {
            data.disabled = true;
        }
        else {
            delete data.disabledCondition;
            delete data.clearDataOnDisable;
        }
        delete data.disabledConfig;
        if (params === null || params === void 0 ? void 0 : params.onSubmit) {
            data = params.onSubmit(data);
        }
        return data;
    };
    return { label: 'Conditional', children, onLoad, onSubmit };
}
function onUpdateAnyValuesBasedOnSiblingConfig(handler, siblingConfig) {
    let reloadValues = false;
    if (siblingConfig && (siblingConfig.values || siblingConfig.valueProvider)) {
        if (siblingConfig.values) {
            if (handler.config.values !== siblingConfig.values) {
                handler.config.values = siblingConfig.values;
                delete handler.config.valueProvider;
                reloadValues = true;
            }
        }
        else if (handler.config.valueProvider !== siblingConfig.valueProvider) {
            delete handler.config.values;
            handler.config.valueProvider = siblingConfig.valueProvider;
            reloadValues = true;
        }
    }
    else if (handler.config.valueProvider) {
        delete handler.config.valueProvider;
        reloadValues = true;
    }
    if (reloadValues) {
        handler.reloadValues();
    }
}
